import { Alert, AlertDescription, Button, Card, CardContent, Spinner } from '@design-system';
import { consumerQuoteCarrier, consumerQuoteKey } from '@shared/consumerPlan';
import { PolicyTermsList } from '../components/policy-terms-list';
import { ReplaceSelectedPolicyDialog } from '../components/replace-selected-policy-dialog';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useCoverageSelection } from '../hooks/use-coverage-selection';
import { useSelectQuote } from '../hooks/use-select-quote';
import { OPTION_LABELS } from '../lib/copy';
import { premiumText } from '../lib/format';
import { quoteDisplayName } from '../lib/policy-name';
import type { ConsumerStepProps } from './step-props';

export function CompareStep({ clientId }: ConsumerStepProps) {
  const { finish } = useConsumerFlow(clientId);
  const { options, projections, plan, scenario } = useCoverageSelection(clientId);
  const selection = useSelectQuote(clientId, () => finish('options'));
  if (!scenario) return null;

  const replacementOption = options.find(
    option => consumerQuoteKey(option.quote) === selection.pendingReplacement?.quoteKey,
  );

  return (
    <StepFrame
      step="compare"
      title="Which trade-off matters most to you?"
      subtitle="The same payment schedule, with the terms side by side."
      onBack={() => finish('options')}
      help={<p>Compare the waiting period, benefit growth, and payment type before deciding. These options don’t have equivalent benefits.</p>}
      primary={{ label: 'Return to options', onClick: () => finish('options') }}
    >
      {selection.chooseFailed && (
        <Alert variant="destructive">
          <AlertDescription>We couldn’t add this option to your plan. Please try again.</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {options.map(option => {
          const key = consumerQuoteKey(option.quote);
          return (
            <Card key={key} className="gap-3 py-4">
              <CardContent className="flex flex-col gap-3 px-4">
                <span className="text-primary text-xs font-semibold uppercase tracking-wide">
                  {OPTION_LABELS[option.label]}
                </span>
                <strong className="text-gray-950">
                  {consumerQuoteCarrier(option.quote)} · {option.quote.productName}
                </strong>
                <span className="text-muted-foreground text-sm">{premiumText(option.quote)}</span>
                <PolicyTermsList
                  quote={option.quote}
                  projection={projections.get(key)}
                  careStartAge={scenario.careStartAge}
                  yearsUntilCare={scenario.yearsUntilCare}
                />
                <Button
                  variant="outline"
                  onClick={() => selection.choose(key)}
                  disabled={!!selection.choosingQuoteKey}
                >
                  {selection.choosingQuoteKey === key && <Spinner />}
                  {plan?.selectedQuoteKey === key ? 'Keep this option' : 'Choose this option'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <ReplaceSelectedPolicyDialog
        replacement={selection.pendingReplacement}
        newPolicyName={replacementOption ? quoteDisplayName(replacementOption.quote) : 'the new option'}
        onConfirm={selection.confirmReplacement}
        onCancel={selection.cancelReplacement}
      />
    </StepFrame>
  );
}
